// 📄 integrationService.ts
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from '../../firebase';
import { CustomDevelopmentData, emptyCustomDevelopment } from '../../types/customDevelopment.ts';
import { TransportConfirmation, emptyTransportConfirmation } from '../../types/transportConfirmation.ts';

/**
 * Guarda la confirmación de pase de OT (transportada + fecha) de UN paso de
 * UN canal de Commerce, para el ambiente QAS o PRD. Se anida bajo el paso
 * de DEV (`channels.{canal}.step_{id}.transport.{env}`) porque DEV es la
 * única fuente de verdad de parámetros/OT/Personalización Z — QAS y PRD ya
 * no guardan su propia copia de esos campos, solo heredan de DEV en modo
 * lectura y registran aquí cuándo se transportó.
 */
export const saveTransportConfirmation = async (
  projectId: string,
  channelKey: string,
  stepKey: string,
  environment: 'QAS' | 'PRD',
  data: TransportConfirmation
) => {
  if (!projectId) throw new Error("ID de proyecto no proporcionado.");

  const projectDocRef = doc(db, "projects", projectId);
  const path = `commerceIntegration.environments.DEV.channels.${channelKey}.${stepKey}.transport.${environment}`;

  await updateDoc(projectDocRef, { [path]: data });
};

/**
 * Registra la fecha en que se guardó manualmente la configuración de UN
 * paso en QAS o PRD — solo aplica a pasos que NO requieren OT, donde cada
 * ambiente mantiene su propia copia independiente de los parámetros (ver
 * "Guardar Ajustes en QAS/PRD" en cada vista de canal). Se guarda en el
 * propio paso de ese ambiente (`environments.{env}.channels.{canal}.
 * step_{id}.configuredAt`), a diferencia de `transport`, que vive bajo DEV.
 */
export const saveManualConfigDate = async (
  projectId: string,
  channelKey: string,
  stepKey: string,
  environment: 'QAS' | 'PRD',
  date: string
) => {
  if (!projectId) throw new Error("ID de proyecto no proporcionado.");

  const projectDocRef = doc(db, "projects", projectId);
  const path = `commerceIntegration.environments.${environment}.channels.${channelKey}.${stepKey}.configuredAt`;

  await updateDoc(projectDocRef, { [path]: date });
};

export interface GeneralsFormData {
  // ERP (on-premise: ECC / S/4HANA On-Premise)
  rfcDestination: string;
  connectionType: string;
  realmId: string;
  apiClientId: string;
  dataCenter: string;

  // ERP (cloud: S/4HANA Private/Public Cloud Edition) — ver esErpCloud en useGeneralConfig.ts
  cloudCommUser: string;
  cloudCommUserEnabled: boolean;
  cloudSystemId: string;
  cloudCommArrangementId: string;
  cloudCommScenarioId: string;
  cloudCigUrl: string;
  cloudApiActivated: boolean;
  cloudTestStatus: string;

  // Cloud Connector
  ccBtpRegion: string;
  ccLocationId: string;
  ccDisplayName: string;
  ccSelectedSubaccountId: string;
  ccCompletedSubaccounts: string[];
  ccMasterHost: string;
  ccVirtualHost: string;
  ccMasterPort: string;
  ccVirtualPort: string;
  ccProtocol: string;
  ccResourcePath: string;
  ccResourceEnabled: boolean;
  ccTunnelSubaccount: string;
  ccTunnelStatus: string;

  // Ariba Portal
  apRealmId: string;
  apIntegrationDir: string;
  apEnablePO: boolean;
  apEnableASN: boolean;
  apEnableInvoice: boolean;
  apEnableReceipt: boolean;
  apTestTargetUrl: string;

  // Managed Gateway (CIG)
  mgProjectId: string;
  mgSystemId: string;
  mgEndpointUrl: string;
  mgAuthType: string;
  mgOrgId: string;
  mgCompanyCode: string;

  // Comunes
  requiresOT: boolean;
  otNumber: string;
  comments: string;
  customDevelopment?: CustomDevelopmentData;
  transport?: { QAS: TransportConfirmation; PRD: TransportConfirmation };
}

export interface PurchaseOrderFormData {
  outputType: string;
  transmissionMedium: string;
  dispatchTime: string;
  partnerNumber: string;
  partnerType: string;
  outboundPort: string;
  distModelName: string;
  senderLogicalSystem: string;
  receiverLogicalSystem: string;
  cigProjectId: string;
  customHeaderFields: boolean;
  customItemFields: boolean;
  testPoNumber: string;
  transmissionStatus: string;
  requiresOT: boolean;
  otNumber: string;
  comments: string;
  customDevelopment?: CustomDevelopmentData;
  transport?: { QAS: TransportConfirmation; PRD: TransportConfirmation };
}

// ─── ASN / Advanced Shipping Notice ────────────────────────────────────────
export interface AsnFormData {
  // Paso 1 — WE20: IDoc Setup
  partnerNumber: string;
  partnerType: string;
  inboundPort: string;
  // Paso 2 — BD51 / WE42: Process Code
  processCode: string;
  functionModule: string;
  autoDeliveryTrigger: boolean;
  // Paso 3 — CIG Mapping
  cigProjectId: string;
  mapShipNoticeId: boolean;
  mapPackaging: boolean;
  mapBatch: boolean;
  mapPoReference: boolean;
  // Paso 4 — VL06I / VL33N: Inbound Delivery
  deliveryType: string;
  referencePlant: string;
  warehouseNumber: string;
  autoDeliveryCreation: boolean;
  adoptionProfile: string;
  // Paso 5 — WE02 / WE05: End-to-End Test
  testShipNoticeId: string;
  generatedDeliveryDoc: string;
  idocMonitorStatus: string;
  // Comunes
  requiresOT: boolean;
  otNumber: string;
  comments: string;
  customDevelopment?: CustomDevelopmentData;
  transport?: { QAS: TransportConfirmation; PRD: TransportConfirmation };
}

export const saveAsnConfig = async (
  projectId: string,
  environment: 'DEV' | 'QAS' | 'PRD',
  activeStep: string,
  formData: AsnFormData
) => {
  if (!projectId) throw new Error("ID de proyecto no proporcionado.");
  if (!environment) throw new Error("Ambiente no seleccionado.");

  const projectDocRef = doc(db, "projects", projectId);
  // ⚡ Clave completamente independiente de purchaseOrder
  const envPath = `commerceIntegration.environments.${environment}`;
  const asnPath = `${envPath}.channels.advancedShippingNotice`;
  const customDevelopment = formData.customDevelopment || emptyCustomDevelopment();
  const transport = formData.transport || { QAS: emptyTransportConfirmation(), PRD: emptyTransportConfirmation() };

  const updates: Record<string, any> = {
    [`${envPath}.checklistProgress.${activeStep}`]: true,
  };

  if (activeStep === 'asn_idoc') {
    updates[`${asnPath}.step_idoc`] = {
      partnerNumber: formData.partnerNumber,
      partnerType: formData.partnerType,
      inboundPort: formData.inboundPort,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'asn_process') {
    updates[`${asnPath}.step_process`] = {
      processCode: formData.processCode,
      functionModule: formData.functionModule,
      autoDeliveryTrigger: formData.autoDeliveryTrigger,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'asn_mapping') {
    updates[`${asnPath}.step_mapping`] = {
      cigProjectId: formData.cigProjectId,
      mapShipNoticeId: formData.mapShipNoticeId,
      mapPackaging: formData.mapPackaging,
      mapBatch: formData.mapBatch,
      mapPoReference: formData.mapPoReference,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'asn_delivery') {
    updates[`${asnPath}.step_delivery`] = {
      deliveryType: formData.deliveryType,
      referencePlant: formData.referencePlant,
      warehouseNumber: formData.warehouseNumber,
      autoDeliveryCreation: formData.autoDeliveryCreation,
      adoptionProfile: formData.adoptionProfile,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'asn_test') {
    updates[`${asnPath}.step_test`] = {
      testShipNoticeId: formData.testShipNoticeId,
      generatedDeliveryDoc: formData.generatedDeliveryDoc,
      idocMonitorStatus: formData.idocMonitorStatus,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }

  await updateDoc(projectDocRef, updates);
};

export const saveGeneralsConfig = async (
  projectId: string,
  environment: 'DEV' | 'QAS' | 'PRD', // 🆕 El ambiente ahora es un parámetro maestro
  activeStep: string,
  formData: GeneralsFormData
) => {
  if (!projectId) throw new Error("ID de proyecto no proporcionado.");
  if (!environment) throw new Error("Ambiente no seleccionado.");

  const projectDocRef = doc(db, "projects", projectId);

  // 🆕 Prefijo dinámico en la base de datos según el ambiente seleccionado
  const envPath = `commerceIntegration.environments.${environment}`;
  const generalsPath = `${envPath}.channels.generals`;
  const customDevelopment = formData.customDevelopment || emptyCustomDevelopment();
  const transport = formData.transport || { QAS: emptyTransportConfirmation(), PRD: emptyTransportConfirmation() };

  const updates: Record<string, any> = {
    // El progreso del checklist ahora es independiente por ambiente
    [`${envPath}.checklistProgress.${activeStep}`]: true,
  };

  // ERP Steps
  if (activeStep === 'rfc') {
    updates[`${generalsPath}.step_rfc`] = {
      rfcDestination: formData.rfcDestination,
      connectionType: formData.connectionType,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'logicalsystem') {
    updates[`${generalsPath}.step_LogicalSystem`] = {
      realmId: formData.realmId,
      apiClientId: formData.apiClientId,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'certificates') {
    updates[`${envPath}.dataCenter`] = formData.dataCenter;
    updates[`${generalsPath}.step_certificates`] = {
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'activation') {
    updates[`${generalsPath}.step_activation`] = {
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  // ERP Cloud Steps (S/4HANA Private/Public Cloud Edition)
  else if (activeStep === 'cloud_comm_user') {
    updates[`${generalsPath}.step_cloud_comm_user`] = {
      cloudCommUser: formData.cloudCommUser,
      cloudCommUserEnabled: formData.cloudCommUserEnabled,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'cloud_system_id') {
    updates[`${generalsPath}.step_cloud_system_id`] = {
      cloudSystemId: formData.cloudSystemId,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'cloud_comm_arrangement') {
    updates[`${generalsPath}.step_cloud_comm_arrangement`] = {
      cloudCommArrangementId: formData.cloudCommArrangementId,
      cloudCommScenarioId: formData.cloudCommScenarioId,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'cloud_cig_url') {
    updates[`${generalsPath}.step_cloud_cig_url`] = {
      cloudCigUrl: formData.cloudCigUrl,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'cloud_comm_scenario') {
    updates[`${generalsPath}.step_cloud_comm_scenario`] = {
      cloudApiActivated: formData.cloudApiActivated,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'cloud_test_connection') {
    updates[`${generalsPath}.step_cloud_test_connection`] = {
      cloudTestStatus: formData.cloudTestStatus,
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  // Cloud Connector Steps
  else if (activeStep === 'cc_subaccount') {
    updates[`${generalsPath}.step_cc_subaccount`] = {
      ccBtpRegion: formData.ccBtpRegion,
      ccLocationId: formData.ccLocationId,
      ccDisplayName: formData.ccDisplayName,
      ccSelectedSubaccountId: formData.ccSelectedSubaccountId,
      ccCompletedSubaccounts: formData.ccCompletedSubaccounts || [],
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'cc_virtual_host') {
    updates[`${generalsPath}.step_cc_virtual_host`] = {
      ccMasterHost: formData.ccMasterHost,
      ccVirtualHost: formData.ccVirtualHost,
      ccMasterPort: formData.ccMasterPort,
      ccVirtualPort: formData.ccVirtualPort,
      ccProtocol: formData.ccProtocol,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'cc_resources') {
    updates[`${generalsPath}.step_cc_resources`] = {
      ccResourcePath: formData.ccResourcePath,
      ccResourceEnabled: formData.ccResourceEnabled,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'cc_tunnel') {
    updates[`${generalsPath}.step_cc_tunnel`] = {
      ccTunnelSubaccount: formData.ccTunnelSubaccount,
      ccTunnelStatus: formData.ccTunnelStatus,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  // Ariba Portal Steps
  else if (activeStep === 'ap_site_config') {
    updates[`${generalsPath}.step_ap_site_config`] = {
      apRealmId: formData.apRealmId,
      apIntegrationDir: formData.apIntegrationDir,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'ap_channels') {
    updates[`${generalsPath}.step_ap_channels`] = {
      apEnablePO: formData.apEnablePO,
      apEnableASN: formData.apEnableASN,
      apEnableInvoice: formData.apEnableInvoice,
      apEnableReceipt: formData.apEnableReceipt,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'ap_testing') {
    updates[`${generalsPath}.step_ap_testing`] = {
      apTestTargetUrl: formData.apTestTargetUrl,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  // Managed Gateway (CIG) Steps
  else if (activeStep === 'mg_project') {
    updates[`${generalsPath}.step_mg_project`] = {
      mgProjectId: formData.mgProjectId,
      mgSystemId: formData.mgSystemId,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'mg_connections') {
    updates[`${generalsPath}.step_mg_connections`] = {
      mgEndpointUrl: formData.mgEndpointUrl,
      mgAuthType: formData.mgAuthType,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'mg_global_mapping') {
    updates[`${generalsPath}.step_mg_global_mapping`] = {
      mgOrgId: formData.mgOrgId,
      mgCompanyCode: formData.mgCompanyCode,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }

  await updateDoc(projectDocRef, updates);
};

export const savePurchaseOrderConfig = async (
  projectId: string,
  environment: 'DEV' | 'QAS' | 'PRD',
  activeStep: string,
  formData: PurchaseOrderFormData
) => {
  if (!projectId) throw new Error("ID de proyecto no proporcionado.");
  if (!environment) throw new Error("Ambiente no seleccionado.");

  const projectDocRef = doc(db, "projects", projectId);
  const envPath = `commerceIntegration.environments.${environment}`;
  const customDevelopment = formData.customDevelopment || emptyCustomDevelopment();
  const transport = formData.transport || { QAS: emptyTransportConfirmation(), PRD: emptyTransportConfirmation() };

  const updates: Record<string, any> = {
    [`${envPath}.checklistProgress.${activeStep}`]: true,
  };

  if (activeStep === 'po_output') {
    updates[`${envPath}.channels.purchaseOrder.step_output`] = {
      outputType: formData.outputType,
      transmissionMedium: formData.transmissionMedium,
      dispatchTime: formData.dispatchTime,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'po_partner') {
    updates[`${envPath}.channels.purchaseOrder.step_partner`] = {
      partnerNumber: formData.partnerNumber,
      partnerType: formData.partnerType,
      outboundPort: formData.outboundPort,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'po_distmodel') {
    updates[`${envPath}.channels.purchaseOrder.step_distmodel`] = {
      distModelName: formData.distModelName,
      senderLogicalSystem: formData.senderLogicalSystem,
      receiverLogicalSystem: formData.receiverLogicalSystem,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'po_mapping') {
    updates[`${envPath}.channels.purchaseOrder.step_mapping`] = {
      cigProjectId: formData.cigProjectId,
      customHeaderFields: formData.customHeaderFields,
      customItemFields: formData.customItemFields,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'po_test') {
    updates[`${envPath}.channels.purchaseOrder.step_test`] = {
      testPoNumber: formData.testPoNumber,
      transmissionStatus: formData.transmissionStatus,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }

  await updateDoc(projectDocRef, updates);
};

// ─── Confirmación de Pedido / Order Confirmation ───────────────────────────
export interface ConfirmationFormData {
  // Paso 1 — SPRO: Maintain Parameters (OC_ENABLED)
  ocEnabledParam: boolean;
  // Paso 2 — Implement Custom Exit (MM06E001)
  exitMM06E001Active: boolean;
  include01Added: boolean;
  include02Added: boolean;
  // Paso 3 — Set Up Confirmation Control
  confirmationControlKey: string;
  ackSequence: string;
  deliverySequence: string;
  // Paso 4 — Schedule Background Job (SM36)
  jobName: string;
  jobVariant: string;
  jobStatus: string;
  // Comunes
  requiresOT: boolean;
  otNumber: string;
  comments: string;
  customDevelopment?: CustomDevelopmentData;
  transport?: { QAS: TransportConfirmation; PRD: TransportConfirmation };
}

export const saveConfirmationConfig = async (
  projectId: string,
  environment: 'DEV' | 'QAS' | 'PRD',
  activeStep: string,
  formData: ConfirmationFormData
) => {
  if (!projectId) throw new Error("ID de proyecto no proporcionado.");
  if (!environment) throw new Error("Ambiente no seleccionado.");

  const projectDocRef = doc(db, "projects", projectId);
  const envPath = `commerceIntegration.environments.${environment}`;
  const confirmPath = `${envPath}.channels.orderConfirmation`;
  const customDevelopment = formData.customDevelopment || emptyCustomDevelopment();
  const transport = formData.transport || { QAS: emptyTransportConfirmation(), PRD: emptyTransportConfirmation() };

  const updates: Record<string, any> = {
    [`${envPath}.checklistProgress.${activeStep}`]: true,
  };

  if (activeStep === 'confirm_params') {
    updates[`${confirmPath}.step_params`] = {
      ocEnabledParam: formData.ocEnabledParam,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'confirm_exit') {
    updates[`${confirmPath}.step_exit`] = {
      exitMM06E001Active: formData.exitMM06E001Active,
      include01Added: formData.include01Added,
      include02Added: formData.include02Added,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'confirm_control') {
    updates[`${confirmPath}.step_control`] = {
      confirmationControlKey: formData.confirmationControlKey,
      ackSequence: formData.ackSequence,
      deliverySequence: formData.deliverySequence,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'confirm_job') {
    updates[`${confirmPath}.step_job`] = {
      jobName: formData.jobName,
      jobVariant: formData.jobVariant,
      jobStatus: formData.jobStatus,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }

  await updateDoc(projectDocRef, updates);
};

// ─── Recepción de Mercancía / Goods Receipt (MIGO) ─────────────────────────
export interface ReceiptFormData {
  // Paso 1 — Define Message Output Control (NACE / SPRO)
  outputTypeGR: string;
  outputProgram: string;
  outputRoutine: string;
  specialFunction: string;
  // Paso 2 — Maintain Parameters (movimientos permitidos)
  allowedMovement101: boolean;
  allowedMovement102: boolean;
  allowedMovement122: boolean;
  // Comunes
  requiresOT: boolean;
  otNumber: string;
  comments: string;
  customDevelopment?: CustomDevelopmentData;
  transport?: { QAS: TransportConfirmation; PRD: TransportConfirmation };
}

export const saveReceiptConfig = async (
  projectId: string,
  environment: 'DEV' | 'QAS' | 'PRD',
  activeStep: string,
  formData: ReceiptFormData
) => {
  if (!projectId) throw new Error("ID de proyecto no proporcionado.");
  if (!environment) throw new Error("Ambiente no seleccionado.");

  const projectDocRef = doc(db, "projects", projectId);
  const envPath = `commerceIntegration.environments.${environment}`;
  const receiptPath = `${envPath}.channels.goodsReceipt`;
  const customDevelopment = formData.customDevelopment || emptyCustomDevelopment();
  const transport = formData.transport || { QAS: emptyTransportConfirmation(), PRD: emptyTransportConfirmation() };

  const updates: Record<string, any> = {
    [`${envPath}.checklistProgress.${activeStep}`]: true,
  };

  if (activeStep === 'receipt_output') {
    updates[`${receiptPath}.step_output`] = {
      outputTypeGR: formData.outputTypeGR,
      outputProgram: formData.outputProgram,
      outputRoutine: formData.outputRoutine,
      specialFunction: formData.specialFunction,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'receipt_params') {
    updates[`${receiptPath}.step_params`] = {
      allowedMovement101: formData.allowedMovement101,
      allowedMovement102: formData.allowedMovement102,
      allowedMovement122: formData.allowedMovement122,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }

  await updateDoc(projectDocRef, updates);
};

// ─── HES / Service Entry Sheet (creación desde Ariba) ──────────────────────
export interface HesFormData {
  // Paso 1 — Maintain Parameters (ENTRYSHEET_CREATE / TIMEOUT)
  entrySheetCreateTimeout: string;
  // Paso 2 — Implement BAdIs / Exits
  badiSesCreateActive: boolean;
  badiSesDeleteActive: boolean;
  // Comunes
  requiresOT: boolean;
  otNumber: string;
  comments: string;
  customDevelopment?: CustomDevelopmentData;
  transport?: { QAS: TransportConfirmation; PRD: TransportConfirmation };
}

export const saveHesConfig = async (
  projectId: string,
  environment: 'DEV' | 'QAS' | 'PRD',
  activeStep: string,
  formData: HesFormData
) => {
  if (!projectId) throw new Error("ID de proyecto no proporcionado.");
  if (!environment) throw new Error("Ambiente no seleccionado.");

  const projectDocRef = doc(db, "projects", projectId);
  const envPath = `commerceIntegration.environments.${environment}`;
  const hesPath = `${envPath}.channels.serviceEntrySheet`;
  const customDevelopment = formData.customDevelopment || emptyCustomDevelopment();
  const transport = formData.transport || { QAS: emptyTransportConfirmation(), PRD: emptyTransportConfirmation() };

  const updates: Record<string, any> = {
    [`${envPath}.checklistProgress.${activeStep}`]: true,
  };

  if (activeStep === 'hes_params') {
    updates[`${hesPath}.step_params`] = {
      entrySheetCreateTimeout: formData.entrySheetCreateTimeout,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'hes_badis') {
    updates[`${hesPath}.step_badis`] = {
      badiSesCreateActive: formData.badiSesCreateActive,
      badiSesDeleteActive: formData.badiSesDeleteActive,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }

  await updateDoc(projectDocRef, updates);
};

// ─── HES - ERP / Service Sheet Response (iniciada desde el ERP) ────────────
export interface HesErpFormData {
  // Paso 1 — Message Output Control (ES / NEU)
  outputTypeES: string;
  outputProgramES: string;
  outputRoutineES: string;
  // Paso 2 — Maintain Parameters (SES_RESPONSE_ENABLED)
  sesResponseEnabled: boolean;
  // Comunes
  requiresOT: boolean;
  otNumber: string;
  comments: string;
  customDevelopment?: CustomDevelopmentData;
  transport?: { QAS: TransportConfirmation; PRD: TransportConfirmation };
}

export const saveHesErpConfig = async (
  projectId: string,
  environment: 'DEV' | 'QAS' | 'PRD',
  activeStep: string,
  formData: HesErpFormData
) => {
  if (!projectId) throw new Error("ID de proyecto no proporcionado.");
  if (!environment) throw new Error("Ambiente no seleccionado.");

  const projectDocRef = doc(db, "projects", projectId);
  const envPath = `commerceIntegration.environments.${environment}`;
  const hesErpPath = `${envPath}.channels.serviceEntrySheetResponse`;
  const customDevelopment = formData.customDevelopment || emptyCustomDevelopment();
  const transport = formData.transport || { QAS: emptyTransportConfirmation(), PRD: emptyTransportConfirmation() };

  const updates: Record<string, any> = {
    [`${envPath}.checklistProgress.${activeStep}`]: true,
  };

  if (activeStep === 'hes_erp_output') {
    updates[`${hesErpPath}.step_output`] = {
      outputTypeES: formData.outputTypeES,
      outputProgramES: formData.outputProgramES,
      outputRoutineES: formData.outputRoutineES,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'hes_erp_params') {
    updates[`${hesErpPath}.step_params`] = {
      sesResponseEnabled: formData.sesResponseEnabled,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }

  await updateDoc(projectDocRef, updates);
};

// ─── Facturas / Invoice (FI-MM) ─────────────────────────────────────────────
export interface InvoiceFormData {
  // Paso 1 — Maintain Parameters
  invoiceEnabled: boolean;
  invoiceParkNoAcc: boolean;
  invHdrPrkNosesPogrxEnb: boolean;
  // Paso 2 — Assign Company Code & Tax Codes
  companyCodeEdi: string;
  taxMappingNotes: string;
  // Paso 3 — Program Parameters & Posting Keys
  glDrPostingKey: string;
  glCrPostingKey: string;
  vendDrPostingKey: string;
  vendCrPostingKey: string;
  invoiceDocType: string;
  // Paso 4 — Customer Exits & BTE Linkages
  exitMRMH0002Active: boolean;
  swe2ObjectType: string;
  swe2Event: string;
  swe2ReceiverFunction: string;
  // Comunes
  requiresOT: boolean;
  otNumber: string;
  comments: string;
  customDevelopment?: CustomDevelopmentData;
  transport?: { QAS: TransportConfirmation; PRD: TransportConfirmation };
}

export const saveInvoiceConfig = async (
  projectId: string,
  environment: 'DEV' | 'QAS' | 'PRD',
  activeStep: string,
  formData: InvoiceFormData
) => {
  if (!projectId) throw new Error("ID de proyecto no proporcionado.");
  if (!environment) throw new Error("Ambiente no seleccionado.");

  const projectDocRef = doc(db, "projects", projectId);
  const envPath = `commerceIntegration.environments.${environment}`;
  const invoicePath = `${envPath}.channels.invoice`;
  const customDevelopment = formData.customDevelopment || emptyCustomDevelopment();
  const transport = formData.transport || { QAS: emptyTransportConfirmation(), PRD: emptyTransportConfirmation() };

  const updates: Record<string, any> = {
    [`${envPath}.checklistProgress.${activeStep}`]: true,
  };

  if (activeStep === 'invoice_params') {
    updates[`${invoicePath}.step_params`] = {
      invoiceEnabled: formData.invoiceEnabled,
      invoiceParkNoAcc: formData.invoiceParkNoAcc,
      invHdrPrkNosesPogrxEnb: formData.invHdrPrkNosesPogrxEnb,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'invoice_tax') {
    updates[`${invoicePath}.step_tax`] = {
      companyCodeEdi: formData.companyCodeEdi,
      taxMappingNotes: formData.taxMappingNotes,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'invoice_posting') {
    updates[`${invoicePath}.step_posting`] = {
      glDrPostingKey: formData.glDrPostingKey,
      glCrPostingKey: formData.glCrPostingKey,
      vendDrPostingKey: formData.vendDrPostingKey,
      vendCrPostingKey: formData.vendCrPostingKey,
      invoiceDocType: formData.invoiceDocType,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'invoice_exits') {
    updates[`${invoicePath}.step_exits`] = {
      exitMRMH0002Active: formData.exitMRMH0002Active,
      swe2ObjectType: formData.swe2ObjectType,
      swe2Event: formData.swe2Event,
      swe2ReceiverFunction: formData.swe2ReceiverFunction,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }

  await updateDoc(projectDocRef, updates);
};

// ─── Estatus de Facturas / Invoice Status Update (ISU) ─────────────────────
export interface InvoiceStatusFormData {
  // Paso 1 — Maintain Parameters
  isuBteEnabled: boolean;
  invoiceDocTypeIsu: string;
  avoidDuplicateIsuPp: boolean;
  // Paso 2 — Configure BTE (FIBF)
  bteProductActive: boolean;
  bteProcess: string;
  bteFunctionModule: string;
  // Paso 3 — Schedule Background Job (SM36)
  jobName: string;
  jobVariant: string;
  jobStatus: string;
  // Comunes
  requiresOT: boolean;
  otNumber: string;
  comments: string;
  customDevelopment?: CustomDevelopmentData;
  transport?: { QAS: TransportConfirmation; PRD: TransportConfirmation };
}

export const saveInvoiceStatusConfig = async (
  projectId: string,
  environment: 'DEV' | 'QAS' | 'PRD',
  activeStep: string,
  formData: InvoiceStatusFormData
) => {
  if (!projectId) throw new Error("ID de proyecto no proporcionado.");
  if (!environment) throw new Error("Ambiente no seleccionado.");

  const projectDocRef = doc(db, "projects", projectId);
  const envPath = `commerceIntegration.environments.${environment}`;
  const isuPath = `${envPath}.channels.invoiceStatus`;
  const customDevelopment = formData.customDevelopment || emptyCustomDevelopment();
  const transport = formData.transport || { QAS: emptyTransportConfirmation(), PRD: emptyTransportConfirmation() };

  const updates: Record<string, any> = {
    [`${envPath}.checklistProgress.${activeStep}`]: true,
  };

  if (activeStep === 'isu_params') {
    updates[`${isuPath}.step_params`] = {
      isuBteEnabled: formData.isuBteEnabled,
      invoiceDocTypeIsu: formData.invoiceDocTypeIsu,
      avoidDuplicateIsuPp: formData.avoidDuplicateIsuPp,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'isu_bte') {
    updates[`${isuPath}.step_bte`] = {
      bteProductActive: formData.bteProductActive,
      bteProcess: formData.bteProcess,
      bteFunctionModule: formData.bteFunctionModule,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }
  else if (activeStep === 'isu_job') {
    updates[`${isuPath}.step_job`] = {
      jobName: formData.jobName,
      jobVariant: formData.jobVariant,
      jobStatus: formData.jobStatus,
      requiresOT: formData.requiresOT,
      otNumber: formData.requiresOT ? formData.otNumber : "",
      comments: formData.comments || "",
      customDevelopment,
      transport
    };
  }

  await updateDoc(projectDocRef, updates);
};

export const getGeneralsConfig = async (projectId: string) => {
  if (!projectId) throw new Error("ID de proyecto no proporcionado.");
  const projectDocRef = doc(db, "projects", projectId);
  const docSnap = await getDoc(projectDocRef);
  if (docSnap.exists()) {
    return docSnap.data().commerceIntegration || null;
  }
  return null;
};

export const saveCutoverConfig = async (
  projectId: string,
  environment: 'DEV' | 'QAS' | 'PRD',
  completedTasks: string[]
) => {
  if (!projectId) throw new Error("ID de proyecto no proporcionado.");
  if (!environment) throw new Error("Ambiente no seleccionado.");

  const projectDocRef = doc(db, "projects", projectId);
  const envPath = `commerceIntegration.environments.${environment}`;

  await updateDoc(projectDocRef, {
    [`${envPath}.cutover.completedTasks`]: completedTasks
  });
};
